import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useCallback, useRef} from 'react'

import {omit} from '../../../../utils/omit'
import type {Repository} from '../../../api/common-contracts'
import {apiApplyTemplate} from '../../../api/memex/api-apply-template'
import type {ApplyTemplateRequest} from '../../../api/memex/contracts'
import {apiRepositoryBulkAddItems} from '../../../api/memex-items/api-repository-bulk-add-items'
import type {RepositoryBulkAddMemexItemsRequest} from '../../../api/memex-items/contracts'
import {DefaultOmitPropertiesForView} from '../../../api/view/contracts'
import {apiCreateWorkflow} from '../../../api/workflows/api-create-workflow'
import {MemexWorkflowContentType} from '../../../api/workflows/contracts'
import type {SelectedTemplate} from '../../../components/template-dialog/hooks/use-template-link'
import useToasts from '../../../components/toasts/use-toasts'
import {getViewTypeParamFromViewType} from '../../../helpers/view-type'
import {isAutoAddWorkflow} from '../../../helpers/workflow-utilities'
import {useApiRequest} from '../../../hooks/use-api-request'
import {useUpdateViewWithoutSettingStates} from '../../../hooks/use-view-apis'
import {useViews} from '../../../hooks/use-views'
import {useBulkAddNotifications} from '../../../state-providers/bulk-add-notifications/bulk-add-notifications-state-provider'
import {useSingleDataRefresh} from '../../../state-providers/data-refresh/use-single-data-refresh'
import {useUpdateMemexSettings} from '../../../state-providers/memex/use-update-memex-settings'
import {useWorkflows} from '../../../state-providers/workflows/use-workflows'
import {BulkCopyResources} from '../../../strings'

const INITIAL_BULK_IMPORT_COUNT = 500

type AutoAddEntityKey = keyof typeof AUTO_ADD_ENTITY_MAP

const AUTO_ADD_ENTITY_MAP = {
  issues: {contentType: MemexWorkflowContentType.Issue, queryEntity: 'issue'},
  prs: {contentType: MemexWorkflowContentType.PullRequest, queryEntity: 'pr'},
} as const

export type ApplyTemplateArgs = {
  title: string
  template: SelectedTemplate
  rollback?: () => void
  autoAddEnabledRepository?: Repository
  autoAddEntities?: Array<AutoAddEntityKey>
  query?: string
  expectedItemCount?: number
  onServerError?: () => void
  onSuccess?: () => void
}

export function deriveAutoAddConfig(autoAddEntities: Array<AutoAddEntityKey> = []) {
  const configs = autoAddEntities.map(entity => AUTO_ADD_ENTITY_MAP[entity])
  return {
    contentTypes: configs.map(config => config.contentType),
    queryEntities: configs.map(config => config.queryEntity),
  }
}

type DerivedAutoAddConfig = ReturnType<typeof deriveAutoAddConfig>

type TemplateActionDeps = {
  applyTemplateRequest: {perform: (req: ApplyTemplateRequest, rollback?: () => void) => Promise<void>}
  viewsMap: ReturnType<typeof useViews>['viewsMap']
  updateView: ReturnType<typeof useUpdateViewWithoutSettingStates>
  workflows: ReturnType<typeof useWorkflows>
}

export function getTemplateActions(
  args: ApplyTemplateArgs,
  {contentTypes, queryEntities}: DerivedAutoAddConfig,
  shouldEnableAutoAddWorkflow: boolean,
  {applyTemplateRequest, viewsMap, updateView, workflows}: TemplateActionDeps,
): Array<Promise<unknown>> {
  const {template, rollback, autoAddEnabledRepository, autoAddEntities, query} = args

  if (template.type !== 'layout') {
    const templateId = template.type === 'custom' ? String(template.template.projectNumber) : template.template.id
    const autoEnableArgs =
      template.type === 'custom'
        ? {}
        : {autoAddEnabledRepositoryId: autoAddEnabledRepository?.id, autoAddEntities, query}
    return [applyTemplateRequest.perform({template: templateId, ...autoEnableArgs}, rollback)]
  }

  const actions: Array<Promise<unknown>> = []

  const view = viewsMap[1]
  if (view) {
    actions.push(
      updateView.perform({
        viewNumber: view.number,
        view: omit(
          {...view.serverViewState, layout: getViewTypeParamFromViewType(template.viewType)},
          DefaultOmitPropertiesForView,
        ),
      }),
    )
  }

  // If we have repository-based auto-add settings, create or update the auto-add workflow
  if (shouldEnableAutoAddWorkflow) {
    const workflow = workflows.workflows.find(isAutoAddWorkflow)
    const getAction = workflow?.actions[0]
    const addAction = workflow?.actions[1]
    if (workflow && getAction && addAction) {
      actions.push(
        apiCreateWorkflow({
          workflow: {
            name: workflow.name,
            enabled: true,
            contentTypes,
            triggerType: workflow.triggerType,
            actions: [
              {
                ...getAction,
                arguments: {
                  repositoryId: autoAddEnabledRepository?.id,
                  query: `is:${queryEntities.join(',')} is:open`,
                },
              },
              {...addAction, arguments: {repositoryId: autoAddEnabledRepository?.id}},
            ],
          },
        }),
      )
    }
  }

  return actions
}

type BulkAddActionDeps = {
  memexCreateProjectFromQuery: boolean
  notifyBulkAddStarted: (requestId: string, isQueryBased?: boolean) => void
  notifyNoItemsToImport: () => void
}

export function getBulkAddActions(
  args: ApplyTemplateArgs,
  {queryEntities}: Pick<DerivedAutoAddConfig, 'queryEntities'>,
  shouldEnableAutoAddWorkflow: boolean,
  {memexCreateProjectFromQuery, notifyBulkAddStarted, notifyNoItemsToImport}: BulkAddActionDeps,
): Array<Promise<unknown>> {
  const {autoAddEnabledRepository, autoAddEntities, query, expectedItemCount} = args
  const userIntendedBulkAdd = Boolean(memexCreateProjectFromQuery && query) || shouldEnableAutoAddWorkflow

  if (!userIntendedBulkAdd) return []

  if (expectedItemCount === 0) {
    // Skip bulk add only if we know for certain there are zero items (expectedItemCount === 0).
    return [Promise.resolve().then(() => notifyNoItemsToImport())]
  }

  const bulkAddRequest: RepositoryBulkAddMemexItemsRequest | undefined =
    memexCreateProjectFromQuery && query
      ? {count: INITIAL_BULK_IMPORT_COUNT, q: query, initialImport: true}
      : autoAddEnabledRepository && autoAddEntities?.length && queryEntities.length
        ? {
            repositoryId: autoAddEnabledRepository.id,
            count: INITIAL_BULK_IMPORT_COUNT,
            contentTypes: queryEntities,
            initialImport: true,
          }
        : undefined

  if (!bulkAddRequest) return []

  return [
    apiRepositoryBulkAddItems(bulkAddRequest).then(([success, requestId]) => {
      if (success && requestId) notifyBulkAddStarted(requestId, !!query)
    }),
  ]
}

export function useApplyTemplate() {
  const handleDataRefresh = useSingleDataRefresh()
  const {addToast} = useToasts()
  const copyingDraftsAsync = useRef(false)
  const memex_create_project_from_query = useFeatureFlag('memex_create_project_from_query')

  const {viewsMap} = useViews()
  const updateView = useUpdateViewWithoutSettingStates()
  const workflows = useWorkflows()
  const {notifyBulkAddStarted, notifyNoItemsToImport} = useBulkAddNotifications()

  const {updateSettings} = useUpdateMemexSettings()
  const applyTemplateRequest = useApiRequest({
    request: async (requestData: ApplyTemplateRequest) => {
      const res = await apiApplyTemplate(requestData)
      if (res.copyingDraftsAsync) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({message: BulkCopyResources.copyingDraftsMessage, type: 'default', keepAlive: false})
        copyingDraftsAsync.current = true
      }
    },
    rollback: (rollbackAction?: () => void) => {
      rollbackAction?.()
    },
    showErrorToast: true,
  })

  const applyTemplate = useCallback(
    async function (args: ApplyTemplateArgs) {
      const {title, autoAddEntities, autoAddEnabledRepository, onServerError, onSuccess} = args
      const autoAddConfig = deriveAutoAddConfig(autoAddEntities)
      const shouldEnableAutoAddWorkflow = Boolean(autoAddEnabledRepository && autoAddEntities?.length)

      const actions: Array<Promise<unknown>> = [
        updateSettings({title, defaultIssueCreateRepository: autoAddEnabledRepository}),
        ...getTemplateActions(args, autoAddConfig, shouldEnableAutoAddWorkflow, {
          applyTemplateRequest,
          viewsMap,
          updateView,
          workflows,
        }),
        ...getBulkAddActions(args, autoAddConfig, shouldEnableAutoAddWorkflow, {
          memexCreateProjectFromQuery: memex_create_project_from_query,
          notifyBulkAddStarted,
          notifyNoItemsToImport,
        }),
      ]

      const results = await Promise.allSettled(actions)

      if (results.some(result => result.status === 'rejected')) {
        onServerError?.()
        return
      }

      // Refresh the project with all new data
      await handleDataRefresh()

      // Call success only if no errors occurred
      onSuccess?.()
    },
    [
      applyTemplateRequest,
      handleDataRefresh,
      updateSettings,
      updateView,
      viewsMap,
      workflows,
      notifyBulkAddStarted,
      notifyNoItemsToImport,
      memex_create_project_from_query,
    ],
  )

  const isApplyingTemplate = applyTemplateRequest.status.current.status === 'loading'

  return {applyTemplate, isApplyingTemplate, copyingDraftsAsync}
}
